/**
 * 🔒 Secure Gemini Proxy Service
 *
 * This service routes all Gemini API calls through the Django backend,
 * keeping API keys secure on the server instead of exposing them in the frontend.
 *
 * MIGRATION NOTE:
 * - This replaces direct Gemini API calls from geminiService.ts
 * - API keys are now stored only on the backend
 * - All Gemini requests go through /api/ai/gemini/generate/
 */

import { logDebug, logInfo, logError } from "@/utils/secureLogger";
import { auth } from "@/firebase";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://elevated-backend-xyaskmbwha-uc.a.run.app/api";

export interface GeminiGenerateRequest {
  prompt: string;
  model?: string; // defaults to gemini-2.5-pro
  systemInstruction?: string;
  temperature?: number; // defaults to 0.7
  maxTokens?: number;
  safetySettings?: any;
}

export interface GeminiGenerateResponse {
  success: boolean;
  text?: string;
  model_used?: string;
  usage?: {
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
  };
  finish_reason?: string;
  error?: string;
  details?: any;
}

/**
 * Generate text using Gemini API via backend proxy
 *
 * @param request - Generation request parameters
 * @returns Generated text response
 */
export async function generateGeminiText(
  request: GeminiGenerateRequest
): Promise<GeminiGenerateResponse> {
  try {
    // Set default model if not provided
    const requestWithDefaults = {
      ...request,
      model: request.model || "gemini-2.5-pro",
    };

    logDebug("Gemini Proxy: Generating text via backend", {
      model: requestWithDefaults.model,
      promptLength: request.prompt.length,
      hasSystemInstruction: !!request.systemInstruction,
    });

    // Get Firebase auth token
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get token (Firebase will automatically refresh if expired)
    // Don't force refresh to avoid clock skew issues
    let idToken = await user.getIdToken(false);

    // Call backend Gemini proxy
    let response = await fetch(`${API_BASE_URL}/ai/gemini/generate/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(requestWithDefaults),
    });

    // If 401, wait for clock sync and retry with refreshed token
    if (response.status === 401) {
      logInfo("🔄 401 detected - waiting 3 seconds for clock sync and refreshing token...");

      // Wait 3 seconds to allow clock skew to resolve
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Force refresh token after waiting
      idToken = await user.getIdToken(true);

      logInfo("✅ Token refreshed after 401, retrying request");

      // Retry request with new token
      response = await fetch(`${API_BASE_URL}/ai/gemini/generate/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestWithDefaults),
      });
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result: GeminiGenerateResponse = await response.json();

    if (result.success) {
      logInfo("Gemini Proxy: Generation successful", {
        model: result.model_used,
        responseLength: result.text?.length || 0,
        tokens: result.usage?.totalTokenCount,
      });
    } else {
      logError("Gemini Proxy: Generation failed", result.error);
    }

    return result;
  } catch (error: any) {
    logError("Gemini Proxy: Request failed", error);
    return {
      success: false,
      error: error.message || "Failed to generate text",
    };
  }
}

/**
 * Helper: Generate text with system instruction
 */
export async function generateWithSystemInstruction(
  prompt: string,
  systemInstruction: string,
  options?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
  }
): Promise<string> {
  const result = await generateGeminiText({
    prompt,
    systemInstruction,
    ...options,
  });

  if (!result.success || !result.text) {
    throw new Error(result.error || "Failed to generate text");
  }

  return result.text;
}

/**
 * Helper: Generate JSON response
 * Automatically parses JSON from Gemini response
 */
export async function generateJSON<T = any>(
  prompt: string,
  options?: {
    systemInstruction?: string;
    model?: string;
    temperature?: number;
  }
): Promise<T> {
  const systemInstruction = options?.systemInstruction
    ? `${options.systemInstruction}\n\nIMPORTANT: Respond ONLY with valid JSON. No markdown, no explanations, just JSON.`
    : "Respond ONLY with valid JSON. No markdown, no explanations, just JSON.";

  const result = await generateGeminiText({
    prompt,
    systemInstruction,
    model: options?.model,
    temperature: options?.temperature,
  });

  if (!result.success || !result.text) {
    throw new Error(result.error || "Failed to generate JSON");
  }

  try {
    // Clean response (remove markdown code blocks if present)
    let cleanText = result.text.trim();
    if (cleanText.startsWith("```json")) {
      cleanText = cleanText.replace(/```json\n?/g, "").replace(/```\n?$/g, "");
    } else if (cleanText.startsWith("```")) {
      cleanText = cleanText.replace(/```\n?/g, "").replace(/```\n?$/g, "");
    }

    return JSON.parse(cleanText) as T;
  } catch (error: any) {
    logError("Failed to parse Gemini JSON response", {
      response: result.text,
      error: error.message,
    });
    throw new Error("Failed to parse JSON response from Gemini");
  }
}

/**
 * Helper: Simple text generation (most common use case)
 */
export async function generate(
  prompt: string,
  options?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
  }
): Promise<string> {
  const result = await generateGeminiText({
    prompt,
    ...options,
  });

  if (!result.success || !result.text) {
    throw new Error(result.error || "Failed to generate text");
  }

  return result.text;
}
